import React from "react";
import { Container, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  CheckCircle,
  Shield,
  Clock,
  TrendingUp,
  AlertCircle,
  Lock,
  Award,
  Calendar,
} from "lucide-react";

const LandingPage = () => {
  const colors = {
    background: "#032530",
    text: "#FFFFFF",
    accent: "#ff8214",
    accentHover: "#ff9b44",
    overlay: "rgba(3, 37, 48, 0.85)",
    cardBg: "rgba(255, 255, 255, 0.05)",
    border: "rgba(255, 130, 20, 0.2)",
  };

  const features = [
    { icon: <Clock size={24} />, text: "Get results in just 60 seconds" },
    {
      icon: <TrendingUp size={24} />,
      text: "Personalized 'Chance Score' based on current trends",
    },
    {
      icon: <AlertCircle size={24} />,
      text: "Identify potential red flags before applying",
    },
    {
      icon: <Shield size={24} />,
      text: "Avoid costly mistakes with profile insights",
    },
    {
      icon: <Lock size={24} />,
      text: "100% private - your data is end to end encrypted",
    },
    {
      icon: <Calendar size={24} />,
      text: "Regularly update with latest visa decisions",
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundImage: `linear-gradient(${colors.overlay}, ${colors.overlay}), 
                        url('https://images.unsplash.com/photo-1541339907198-e08756dedf3f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        padding: "0 20px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Elements */}
      <div
        style={{
          position: "absolute",
          top: "10%",
          left: "5%",
          width: "200px",
          height: "200px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${colors.accent}20 0%, transparent 70%)`,
          animation: "pulse 4s infinite alternate",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "15%",
          right: "10%",
          width: "150px",
          height: "150px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${colors.accent}15 0%, transparent 70%)`,
          animation: "pulse 3s infinite alternate-reverse",
        }}
      />

      <Container className="py-5">
        {/* Main Header Section */}
        <div className="text-center mb-5">
          {/* Badge */}
          <div
            style={{
              display: "inline-block",
              backgroundColor: colors.accent,
              color: colors.background,
              padding: "8px 25px",
              borderRadius: "50px",
              fontSize: "0.9rem",
              fontWeight: "600",
              marginBottom: "2rem",
              letterSpacing: "1px",
              boxShadow: `0 4px 15px ${colors.accent}40`,
            }}
          >
            <CheckCircle
              size={16}
              style={{ marginRight: "8px", verticalAlign: "middle" }}
            />
            TRUSTED BY THOUSANDS OF STUDENTS
          </div>

          {/* Main Headline */}
          <h1
            style={{
              color: colors.text,
              fontSize: "4rem",
              fontWeight: "800",
              marginBottom: "1rem",
              textShadow: "0 4px 20px rgba(0, 0, 0, 0.7)",
              lineHeight: "1.2",
            }}
          >
            Irish Dreams?
            <br />
            <span
              style={{
                color: colors.accent,
                textShadow: `0 0 30px ${colors.accent}80`,
              }}
            >
              Check Your Study Visa Odds
            </span>
            <br />
            in 60 Seconds!
          </h1>

          {/* Sub Headline */}
          <h2
            style={{
              color: colors.text,
              fontSize: "1.8rem",
              fontWeight: "600",
              marginBottom: "3rem",
              opacity: "0.95",
              textShadow: "0 2px 10px rgba(0, 0, 0, 0.5)",
            }}
          >
            Don't Leave Your Ireland Study Visa to Luck!
          </h2>
        </div>

        <div className="row align-items-center">
          {/* Left Column - Features */}
          <div className="col-lg-6 mb-5 mb-lg-0">
            <div
              style={{
                backgroundColor: colors.cardBg,
                backdropFilter: "blur(10px)",
                borderRadius: "20px",
                padding: "40px",
                border: `1px solid ${colors.border}`,
                boxShadow: `0 20px 40px rgba(0, 0, 0, 0.3)`,
                height: "100%",
              }}
            >
              <h3
                style={{
                  color: colors.accent,
                  fontSize: "1.8rem",
                  fontWeight: "700",
                  marginBottom: "30px",
                  textAlign: "center",
                }}
              >
                Why Use Our Assessment Tool?
              </h3>

              <ul style={{ listStyle: "none", padding: 0 }}>
                {features.map((feature, index) => (
                  <li
                    key={index}
                    style={{
                      marginBottom: "20px",
                      padding: "15px",
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "12px",
                      borderLeft: `4px solid ${colors.accent}`,
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateX(10px)";
                      e.currentTarget.style.backgroundColor =
                        "rgba(255, 130, 20, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateX(0)";
                      e.currentTarget.style.backgroundColor =
                        "rgba(255, 255, 255, 0.05)";
                    }}
                  >
                    <div
                      style={{
                        color: colors.accent,
                        marginRight: "15px",
                        minWidth: "24px",
                      }}
                    >
                      {feature.icon}
                    </div>
                    <span
                      style={{
                        color: colors.text,
                        fontSize: "1.1rem",
                        lineHeight: "1.5",
                      }}
                    >
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Key Benefits */}
              <div
                className="mt-5 pt-4"
                style={{ borderTop: `1px solid ${colors.border}` }}
              >
                <div className="row text-center">
                  <div className="col-4">
                    <Award
                      size={32}
                      style={{ color: colors.accent, marginBottom: "10px" }}
                    />
                    <div style={{ color: colors.text, fontWeight: "600" }}>
                      100% Free
                    </div>
                  </div>
                  <div className="col-4">
                    <Shield
                      size={32}
                      style={{ color: colors.accent, marginBottom: "10px" }}
                    />
                    <div style={{ color: colors.text, fontWeight: "600" }}>
                      Secure & Private
                    </div>
                  </div>
                  <div className="col-4">
                    <TrendingUp
                      size={32}
                      style={{ color: colors.accent, marginBottom: "10px" }}
                    />
                    <div style={{ color: colors.text, fontWeight: "600" }}>
                      Data-Driven
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - CTA */}
          <div className="col-lg-6">
            <div
              style={{
                backgroundColor: colors.cardBg,
                backdropFilter: "blur(10px)",
                borderRadius: "20px",
                padding: "50px 40px",
                border: `1px solid ${colors.border}`,
                boxShadow: `0 20px 40px rgba(0, 0, 0, 0.3)`,
                textAlign: "center",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "800",
                  color: colors.accent,
                  marginBottom: "20px",
                  lineHeight: "1.2",
                }}
              >
                Stop Guessing
                <br />
                Start Planning
              </div>

              <p
                style={{
                  color: colors.text,
                  fontSize: "1.3rem",
                  marginBottom: "40px",
                  opacity: "0.9",
                  lineHeight: "1.6",
                }}
              >
                It's Fast, It's Online, it's private and above all,
                <br />
                <strong style={{ color: colors.accent, fontSize: "1.5rem" }}>
                  It's Free!
                </strong>
              </p>

              {/* Main CTA Button */}
              <Button
                as={Link}
                to="/assessment"
                size="lg"
                style={{
                  backgroundColor: colors.accent,
                  borderColor: colors.accent,
                  color: colors.background,
                  borderRadius: "50px",
                  padding: "18px 60px",
                  fontSize: "1.3rem",
                  fontWeight: "700",
                  transition: "all 0.3s ease",
                  boxShadow: `0 10px 30px ${colors.accent}60`,
                  textTransform: "uppercase",
                  letterSpacing: "1.5px",
                  marginBottom: "30px",
                  width: "100%",
                  maxWidth: "100%",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.accentHover;
                  e.currentTarget.style.borderColor = colors.accentHover;
                  e.currentTarget.style.transform =
                    "translateY(-5px) scale(1.05)";
                  e.currentTarget.style.boxShadow = `0 15px 40px ${colors.accentHover}80`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = colors.accent;
                  e.currentTarget.style.borderColor = colors.accent;
                  e.currentTarget.style.transform = "translateY(0) scale(1)";
                  e.currentTarget.style.boxShadow = `0 10px 30px ${colors.accent}60`;
                }}
              >
                Start Free Assessment
              </Button>

              {/* Countdown Timer Visual */}
              <div
                style={{
                  backgroundColor: "rgba(255, 130, 20, 0.1)",
                  borderRadius: "15px",
                  padding: "15px",
                  marginBottom: "30px",
                  border: `2px dashed ${colors.accent}`,
                }}
              >
                <div
                  style={{
                    color: colors.accent,
                    fontSize: "1.1rem",
                    fontWeight: "600",
                    marginBottom: "5px",
                  }}
                >
                  ⏱️ Get Your Results In
                </div>
                <div
                  style={{
                    color: colors.text,
                    fontSize: "2.5rem",
                    fontWeight: "800",
                    fontFamily: "monospace",
                  }}
                >
                  60 SECONDS
                </div>
                
              </div>
              
            </div>
            
          </div>
           {/* Disclaimer Note - Prominent Highlight */}
           <div
                      style={{
                        fontSize: "0.85rem",
                        color: "#ffd700",
                        backgroundColor: "rgba(255, 215, 0, 0.1)",
                        textAlign: "center",
                        border: "1px solid rgba(255, 215, 0, 0.3)",
                        borderRadius: "8px",
                        padding: "1.25rem",
                        marginTop: "1.5rem",
                        lineHeight: "1.6",
                        fontWeight: "500",
                        letterSpacing: "0.3px",
                        boxShadow: "0 4px 12px rgba(255, 215, 0, 0.1)",
                      }}
                    >
                      <span
                        style={{
                          display: "block",
                          fontSize: "0.9rem",
                          fontWeight: "700",
                          marginBottom: "0.5rem",
                          textTransform: "uppercase",
                          letterSpacing: "1px",
                          color: "#ffd700",
                        }}
                      >
                        ⚠️ Important Disclaimer
                      </span>
                      The outcomes provided by this assessment are intended for
                      preliminary guidance only and do not constitute a
                      guarantee of visa issuance or legal success. This platform
                      operates as an independent consultancy and maintains no
                      formal affiliation, endorsement, or partnership with the
                      Government of Ireland or any statutory immigration
                      authorities.
                    </div>
        </div>

        <style jsx="true">{`
          @keyframes pulse {
            0% {
              transform: scale(1);
              opacity: 0.6;
            }
            100% {
              transform: scale(1.1);
              opacity: 0.8;
            }
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes float {
            0%,
            100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
          }

          h1,
          h2,
          .feature-card,
          .cta-box {
            animation: fadeInUp 0.8s ease-out;
          }

          .cta-box {
            animation-delay: 0.2s;
          }

          .feature-card:nth-child(1) {
            animation-delay: 0.1s;
          }
          .feature-card:nth-child(2) {
            animation-delay: 0.2s;
          }
          .feature-card:nth-child(3) {
            animation-delay: 0.3s;
          }
          .feature-card:nth-child(4) {
            animation-delay: 0.4s;
          }
          .feature-card:nth-child(5) {
            animation-delay: 0.5s;
          }
          .feature-card:nth-child(6) {
            animation-delay: 0.6s;
          }

          .main-button {
            animation: float 3s ease-in-out infinite;
            animation-delay: 1s;
          }

          ::-webkit-scrollbar {
            width: 10px;
          }

          ::-webkit-scrollbar-track {
            background: #0b2c36;
          }

          ::-webkit-scrollbar-thumb {
            background: ${colors.accent};
            border-radius: 5px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: ${colors.accentHover};
          }

          @media (max-width: 992px) {
            h1 {
              font-size: 3rem !important;
            }

            h2 {
              font-size: 1.5rem !important;
            }
          }

          @media (max-width: 768px) {
            h1 {
              font-size: 2.5rem !important;
            }

            .feature-item {
              font-size: 1rem !important;
            }

            .stats-bar div {
              font-size: 2rem !important;
            }
          }

          @media (max-width: 576px) {
            h1 {
              font-size: 2rem !important;
            }

            .cta-box {
              padding: 30px 20px !important;
            }

            .main-button {
              width: 100% !important;
              padding: 15px 20px !important;
            }
          }
        `}</style>
      </Container>
    </div>
  );
};

export default LandingPage;
